import { Component, inject, signal, computed, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterOutlet, RouterLink, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { ConnectivityService } from './services/connectivity.service';
import { OfflineQueueService } from './services/offline-queue.service';
import { SyncService } from './services/sync.service';
import { WebSocketService } from './services/websocket.service';
import { AuthService } from './services/auth.service';
import { ClockService } from './services/clock.service';
import { ApiService } from './services/api.service';
import { NotificationBannerComponent } from './components/notification-banner/notification-banner.component';
import { InstallPromptComponent } from './components/install-prompt/install-prompt.component';
import { VersionBadgeComponent } from './components/version-badge/version-badge.component';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, RouterLink, TranslateModule, NotificationBannerComponent, InstallPromptComponent, VersionBadgeComponent],
    template: `
    <div class="app-shell">
      @if (!connectivity.isOnline()) {
        <div
          class="connectivity-indicator offline"
          role="status"
          [attr.aria-label]="'common.offline' | translate"
        >
          <span class="indicator-dot"></span>
          <span class="indicator-text">{{ 'common.offline' | translate }}</span>
        </div>
      }
      @if (pendingCount() > 0) {
        <div
          class="pending-indicator"
          role="status"
          [attr.aria-label]="'common.pending_actions' | translate:{ count: pendingCount() }"
        >
          <span class="pending-badge">{{ pendingCount() }}</span>
          <span class="pending-text">{{ 'common.pending_actions' | translate:{ count: pendingCount() } }}</span>
        </div>
      }
      <app-notification-banner />
      <app-install-prompt />
      <main>
        <router-outlet />
      </main>
      <!-- Build/version badge: click for version + release date + stale-build check. -->
      <app-version-badge [aboveNav]="showBottomNav()" />
      @if (showBottomNav()) {
        <nav class="bottom-nav" aria-label="Main navigation">
          <a routerLink="/dashboard" class="nav-item" [class.active]="currentRoute() === '/dashboard'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            <span class="nav-label">{{ 'dashboard.title' | translate }}</span>
          </a>
          <a routerLink="/bookings" class="nav-item" [class.active]="currentRoute().startsWith('/bookings')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <span class="nav-label">{{ 'bookings.title' | translate }}</span>
          </a>
          <a routerLink="/admin" class="nav-item" [class.active]="currentRoute() === '/admin'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
            <span class="nav-label">{{ 'admin.title' | translate }}</span>
          </a>
          <a routerLink="/hr-assistant" class="nav-item" [class.active]="currentRoute() === '/hr-assistant'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <span class="nav-label">{{ 'hrAssistant.navLabel' | translate }}</span>
          </a>
          <a routerLink="/settings" class="nav-item" [class.active]="currentRoute() === '/settings'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            <span class="nav-label">{{ 'settings.title' | translate }}</span>
          </a>
        </nav>
      }
    </div>
  `,
    styles: [
        `
      .app-shell {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }

      .connectivity-indicator {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 12px;
        font-size: 12px;
        font-weight: 500;
        transition: background-color 0.3s ease;
      }

      .connectivity-indicator.offline {
        background-color: #fbe9e7;
        color: #c62828;
      }

      :host-context(.dark) .connectivity-indicator.offline {
        background-color: rgba(196,87,61,0.12);
        color: #E07A63;
      }

      .indicator-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #f44336;
      }

      .indicator-text {
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .pending-indicator {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 12px;
        font-size: 12px;
        font-weight: 500;
        background-color: #fff3e0;
        color: #e65100;
      }

      :host-context(.dark) .pending-indicator {
        background-color: rgba(224,162,61,0.12);
        color: #E8B15A;
      }

      .pending-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        border-radius: 9px;
        background-color: #e65100;
        color: #fff;
        font-size: 11px;
        font-weight: 700;
        padding: 0 4px;
      }

      .pending-text {
        letter-spacing: 0.3px;
      }

      main {
        flex: 1;
        padding-bottom: 72px;
      }

      .bottom-nav {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        display: flex;
        justify-content: space-around;
        align-items: center;
        background: var(--surface, #FFFFFF);
        border-top: 1px solid var(--line, rgba(23,26,30,0.08));
        padding: 8px 0 env(safe-area-inset-bottom, 8px);
        z-index: 1000;
        box-shadow: 0 -2px 8px rgba(0,0,0,0.06);
      }

      .nav-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
        text-decoration: none;
        color: var(--ink-soft, #5B5F63);
        font-size: 11px;
        font-weight: 500;
        padding: 4px 12px;
        border-radius: 8px;
        transition: color 0.2s, background 0.2s;
      }

      .nav-item.active {
        color: var(--pine, #2F6F5E);
      }

      .nav-item svg {
        width: 22px;
        height: 22px;
      }

      .nav-label {
        font-size: 10px;
        letter-spacing: 0.02em;
      }

      :host-context(.dark) .bottom-nav {
        background: var(--surface, #1B1F26);
        border-top-color: rgba(236,237,238,0.08);
      }

      :host-context(.dark) .nav-item {
        color: var(--ink-soft, #9CA3AC);
      }

      :host-context(.dark) .nav-item.active {
        color: var(--pine, #4FA98D);
      }
    `,
    ]
})
export class AppComponent implements OnInit, OnDestroy {
  readonly connectivity = inject(ConnectivityService);
  private readonly offlineQueue = inject(OfflineQueueService);
  private readonly router = inject(Router);
  private readonly syncService = inject(SyncService);
  private readonly webSocketService = inject(WebSocketService);
  private readonly authService = inject(AuthService);
  private readonly clockService = inject(ClockService);
  private readonly api = inject(ApiService);

  readonly pendingCount = signal(0);
  readonly currentRoute = signal('');
  readonly showBottomNav = computed(() => {
    const route = this.currentRoute();
    const unauthRoutes = ['/login', '/forgot-password', '/force-change-password'];
    return this.authService.isAuthenticated() && !unauthRoutes.includes(route);
  });

  private pollInterval: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    // Apply saved theme globally at app start so every page (not just the
    // dashboard) reflects dark mode, regardless of the entry route.
    try {
      if (localStorage.getItem('pwa-theme') === 'dark') {
        document.body.classList.add('dark');
      }
    } catch {
      // localStorage unavailable (private browsing) — default to light.
    }

    this.refreshPendingCount();
    this.pollInterval = setInterval(() => this.refreshPendingCount(), 5000);

    // After offline sync completes, refresh clock state from server
    this.syncService.onSyncComplete = () => {
      this.refreshClockStateFromServer();
    };

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {
      const url = event.urlAfterRedirects || event.url;
      const path = url.split('?')[0];

      // Track current route for bottom navigation active state
      this.currentRoute.set(path);

      // Connect WebSocket after navigation if authenticated but not yet connected
      if (this.authService.isAuthenticated() && !this.webSocketService.isConnected()) {
        this.webSocketService.connect();
      }

      // After navigating to dashboard (post-login), trigger sync for any queued actions
      if (path === '/dashboard' && this.authService.isAuthenticated() && this.connectivity.isOnline()) {
        this.syncService.triggerSync();
      }
    });

    // Connect WebSocket when user is already authenticated at app start
    // (handles the case where user refreshes while already logged in)
    if (this.authService.isAuthenticated()) {
      this.webSocketService.connect();
    }
  }

  ngOnDestroy(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  /**
   * After sync completes, fetch the resource status from the server
   * to confirm the clock state is correct.
   */
  private refreshClockStateFromServer(): void {
    const tenantId = this.authService.tenantId?.();
    const resourceId = this.authService.resourceId?.();
    if (!tenantId || !resourceId) return;

    // Small delay to let the server process the synced clock event
    setTimeout(() => {
      this.api.get<any>(`/tenants/${tenantId}/resources/${resourceId}`).subscribe({
        next: (res) => {
          if (res.ok && res.data) {
            const status = res.data.resource_status;
            const clockedInStatuses = ['CLOCK_IN', 'AT_JOB_SITE', 'ON_ROUTE', 'ON_BREAK', 'BUSY'];
            this.clockService.setClockedIn(clockedInStatuses.includes(status));
          }
        },
        error: () => {} // Silently fail
      });
    }, 1500);
  }

  /** Refresh the pending action count from IndexedDB */
  refreshPendingCount(): void {
    this.offlineQueue.getCount().then((count) => {
      this.pendingCount.set(count);
    }).catch(() => {
      // Silently ignore IndexedDB errors for the indicator
    });
  }
}
